import * as React from "react";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { detectMediaKind } from "@/lib/media-kind";
import { storageImage, storageSrcSet } from "@/lib/image-url";
import {
  VideoControlsBar,
  videoControlButtonClass,
  videoControlIconClass,
} from "@/components/media/VideoControlsBar";


/**
 * HeroMedia — يعرض فيديو خلفيّ من Lovable Cloud Storage مع منع كامل للومضات
 * وتكيّف ذكيّ مع الشبكات البطيئة + زرّ تحكّم اختياريّ للمستخدم.
 *
 * منع الومضة:
 *  1. الحاوية لها خلفيّة سوداء + poster كـ background-image (يظهر فوراً).
 *  2. عنصر <video> يستخدم خاصيّة `poster` الأصليّة (قبل أوّل إطار).
 *  3. لا opacity transitions — الـposter تحت الفيديو يطابق إطاره الأوّل.
 *
 * تكيّف الشبكة (Network Information API):
 *  - slow-2g/2g/slow-3g/saveData → poster فقط، لا metadata.
 *  - تحسّن الاتصال → نركّب الفيديو ونشغّله تلقائيّاً.
 *
 * زرّ التحكّم يوقف أو يشغّل الفيديو الحالي فقط؛ لا يحفظ تعطيل الفيديو
 * على مستوى الموقع كي تبدأ كلّ الفيديوهات تلقائيّاً عند فتح أيّ صفحة.
 *
 * iOS Safari autoplay: muted + playsInline + autoplay (الكل مطلوب).
 */
export interface HeroMediaProps {
  videoUrl: string;
  /** نسخة الموبايل (<768px). عند التوفّر يضاف <source media="(max-width: 767px)"> ليختار المتصفّح بنفسه — بدون JS لتجنّب hydration mismatch. */
  mobileVideoUrl?: string;
  poster: string;
  mobilePoster?: string;
  alt: string;
  className?: string;
  /** أظهر زرّ تشغيل/إيقاف الفيديو (افتراضيّاً false لتجنّب التكرار في PageHero). */
  showToggle?: boolean;
  /** تشغيل يدويّ فقط — يظهر البوستر أوّلاً ولا يبدأ الفيديو إلّا عند الضغط على زر التشغيل. */
  manualPlay?: boolean;
  /**
   * ⚡ LCP: صورة الهيرو الآتية من السيرفر (loader) لترسم داخل أوّل HTML
   * قبل وصول استعلام العميل. تُستبدل تلقائيّاً حين تصل بيانات الأدمن.
   */
  initialImageDesktop?: string | null;
  initialImageMobile?: string | null;
}

type NetworkConnection = {
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  saveData?: boolean;
  addEventListener?: (type: "change", listener: () => void) => void;
  removeEventListener?: (type: "change", listener: () => void) => void;
};

function getConnection(): NetworkConnection | null {
  if (typeof navigator === "undefined") return null;
  const nav = navigator as Navigator & {
    connection?: NetworkConnection;
    mozConnection?: NetworkConnection;
    webkitConnection?: NetworkConnection;
  };
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection ?? null;
}

function isSlowNetwork(conn: NetworkConnection | null): boolean {
  if (!conn) return false;
  if (conn.saveData) return true;
  const t = conn.effectiveType;
  return t === "slow-2g" || t === "2g" || (t as string) === "slow-3g";
}

export function HeroMedia({
  videoUrl,
  mobileVideoUrl,
  poster,
  mobilePoster,
  alt,
  className,
  showToggle = false,
  manualPlay = true,
  initialImageDesktop = null,
  initialImageMobile = null,
}: HeroMediaProps) {
  const { t } = useLocale();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [videoFailed, setVideoFailed] = React.useState(false);
  const [networkOk, setNetworkOk] = React.useState(true);
  const [userEnabled, setUserEnabled] = React.useState(true);
  const [manualStarted, setManualStarted] = React.useState(false);
  // الصوت: التشغيل التلقائيّ يبقى صامتاً (سياسة المتصفّحات)،
  // أمّا التشغيل اليدويّ بنقرة المستخدم فيبدأ بصوت.
  const [soundOn, setSoundOn] = React.useState(false);

  // حالة الشبكة
  React.useEffect(() => {
    const conn = getConnection();
    const evaluate = () => setNetworkOk(!isSlowNetwork(conn));
    evaluate();
    conn?.addEventListener?.("change", evaluate);
    return () => conn?.removeEventListener?.("change", evaluate);
  }, []);

  // تمييز نوع كلّ وسيط (فيديو/صورة) من الامتداد
  const kindDesktop = detectMediaKind(videoUrl);
  const kindMobile = detectMediaKind(mobileVideoUrl);
  const hasVideo =
    (kindDesktop === "video" && Boolean(videoUrl)) ||
    (kindMobile === "video" && Boolean(mobileVideoUrl));
  // رابط فيديو فعليّ لعنصر <video> (لا نمرّر صورة كـsource)
  const videoSrcDesktop = kindDesktop === "video" ? videoUrl : "";
  const videoSrcMobile = kindMobile === "video" && mobileVideoUrl ? mobileVideoUrl : "";

  // اختيار نسخة الجوّال في JS: خاصيّة `media` على <source> داخل <video>
  // مُهملة في معظم المتصفّحات (بخلاف <picture>)، فكانت نسخة الجوّال تُختار
  // أو تُهمَل عشوائيّاً. الاختيار يحدث بعد mount فقط ⇒ لا hydration mismatch.
  const [isNarrow, setIsNarrow] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  const activePoster = (isNarrow ? mobilePoster || poster : poster) || "";

  // صور بديلة عن الفيديو (تظهر فوق الـposter قبل/بدون الفيديو)
  // قبل وصول بيانات العميل: نستخدم صورة السيرفر فوراً (لا انتظار، لا شاشة سوداء).
  const imageDesktop =
    kindDesktop === "image" ? videoUrl : videoUrl ? null : initialImageDesktop || null;
  const imageMobile =
    kindMobile === "image"
      ? mobileVideoUrl
      : mobileVideoUrl
        ? null
        : initialImageMobile || initialImageDesktop || null;
  // مصدر واحد فعليّ لكلّ مقاس. ممنوع تبديل فيديو المقاس الآخر إذا كان لهذا
  // المقاس صورة خاصّة به (وإلّا ظهر فيديو الموبايل على الديسكتوب رغم الصورة).
  const activeVideoSrc =
    (isNarrow
      ? videoSrcMobile || (imageMobile ? "" : videoSrcDesktop)
      : videoSrcDesktop || (imageDesktop ? "" : videoSrcMobile)) || "";

  /**
   * هل لدينا صورة ثابتة نعرضها بدل الفيديو؟
   * إن لم توجد (لا poster ولا صورة بديلة) فإخفاء الفيديو = شاشة سوداء.
   * لذلك نتجاهل حالة الشبكة البطيئة/Data Saver في هذه الحالة تحديداً.
   * (Network Information API موجود على Android Chrome فقط — لهذا كان الهيرو
   *  أسود على أندرويد ويعمل على iOS.)
   */
  const hasStill = Boolean(activePoster) || Boolean(imageDesktop) || Boolean(imageMobile);
  const allowVideo =
    hasVideo &&
    Boolean(activeVideoSrc) &&
    (manualPlay || userEnabled) &&
    !videoFailed &&
    (networkOk || !hasStill) &&
    (!manualPlay || manualStarted);



  // تشغيل الفيديو حين يكون مركّباً
  React.useEffect(() => {
    if (!allowVideo) return;
    const video = videoRef.current;
    if (!video) return;

    video.muted = !soundOn;
    video.defaultMuted = !soundOn;
    if (!soundOn) video.setAttribute("muted", "");
    else video.removeAttribute("muted");
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.setAttribute("x5-playsinline", "");
    video.setAttribute("x5-video-player-type", "h5-page");

    const tryPlay = () => {
      void video.play().catch(() => {
        // عند منع التشغيل يبقى الـposter ظاهراً ويمكن للمستخدم إعادة المحاولة.
      });
    };
    tryPlay();
    // Android Chrome قد يرفض التشغيل قبل جهوزيّة البيانات — نعيد المحاولة
    video.addEventListener("loadeddata", tryPlay);
    video.addEventListener("canplay", tryPlay);
    return () => {
      video.removeEventListener("loadeddata", tryPlay);
      video.removeEventListener("canplay", tryPlay);
    };
  }, [allowVideo, activeVideoSrc, soundOn]);


  const toggle = () => {
    if (manualPlay) {
      const next = !manualStarted;
      const video = videoRef.current;
      if (next) {
        setVideoFailed(false);
        setManualStarted(true);
        if (video) {
          // النقرة اليدويّة تسمح بالصوت → نشغّل بصوت مباشرة.
          setSoundOn(true);
          video.muted = false;
          video.defaultMuted = false;
          video.removeAttribute("muted");
          video.volume = 1;
          video.setAttribute("playsinline", "");
          // يجب أن يبدأ play() داخل نقرة المستخدم نفسها؛ تأجيله إلى effect
          // يجعل iOS وبعض متصفحات Android ترفض التشغيل اليدوي.
          void video.play().catch(() => {
            // بعض المتصفّحات ترفض الصوت → نعيد المحاولة صامتاً كي لا يفشل التشغيل.
            setSoundOn(false);
            video.muted = true;
            video.setAttribute("muted", "");
            void video.play().catch(() => setManualStarted(false));
          });
        }
      } else {
        video?.pause();
        setManualStarted(false);
      }
      return;
    }
    const next = !userEnabled;
    setUserEnabled(next);
    // إعادة المحاولة: لو فشل تحميل الفيديو سابقاً، الضغط على «تشغيل» يعيد تركيبه.
    if (next) setVideoFailed(false);
  };


  // isReady: لدينا ما نعرضه فعلاً (poster أو فيديو أو صورة بديلة).
  // قبل ذلك → الحاوية السوداء فقط، لا <img src=""> ولا <video> فارغ → لا فلاش.
  const hasManualVideo = manualPlay && hasVideo && Boolean(activeVideoSrc) && !videoFailed;
  const isReady = Boolean(activePoster) || allowVideo || hasManualVideo || Boolean(imageDesktop) || Boolean(imageMobile);

  return (
    <div
      className="absolute inset-0 h-full w-full overflow-hidden"
      style={{
        // الهيرو طبقة مستقلّة ومصمتة؛ خلفيّة الصفحة تبدأ بصريّاً بعده.
        backgroundColor: "var(--onyx)",
      }}
      data-hero-ready={isReady ? "1" : "0"}
    >

      {!isReady ? null : hasVideo && (allowVideo || hasManualVideo) ? (
        <video
          key={activeVideoSrc}
          src={activeVideoSrc}
          ref={videoRef}
          autoPlay={!manualPlay}
          muted={!soundOn}
          loop
          playsInline
          poster={activePoster || undefined}
          {...({ "webkit-playsinline": "", "x5-playsinline": "" } as Record<string, string>)}
          preload={manualPlay ? "none" : hasStill ? "metadata" : "auto"}
          aria-label={alt}
          onError={() => setVideoFailed(true)}
          className={`absolute inset-0 h-full w-full object-cover object-[72%_center] md:object-center ${className ?? ""}`}
        />
      ) : imageMobile || imageDesktop || activePoster ? (
        <picture>
          {imageMobile ? (
            <source
              media="(max-width: 767px)"
              srcSet={storageSrcSet(imageMobile, [420, 560, 750, 1000]) ?? imageMobile}
              sizes="100vw"
            />
          ) : null}
          {imageDesktop ? (
            <source
              media="(min-width: 768px)"
              srcSet={storageSrcSet(imageDesktop, [1024, 1440, 1920]) ?? imageDesktop}
              sizes="100vw"
            />
          ) : null}
          <img
            src={
              imageDesktop
                ? storageImage(imageDesktop, 1440)
                : imageMobile
                  ? storageImage(imageMobile, 750)
                  : activePoster
            }

            alt={alt}
            width={971}
            height={1620}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className={`absolute inset-0 h-full w-full object-cover object-[72%_center] md:object-center ${className ?? ""}`}
          />
        </picture>
      ) : null}

      {/* شريط تحكّم موحَّد: تشغيل ثمّ صوت، أسفل اليسار */}
      {(showToggle || manualPlay) && hasVideo && networkOk ? (
        <VideoControlsBar>
          {/* الزرّ يبقى ظاهراً حتى لو فشل تحميل الفيديو، فالضغط عليه = إعادة محاولة */}
          <button
            type="button"
            onClick={toggle}
            aria-label={allowVideo ? t("hero.video.pause") : t("hero.video.play")}
            title={allowVideo ? t("hero.video.pause") : t("hero.video.play")}
            className={videoControlButtonClass()}
          >
            {allowVideo ? (
              <Pause className={videoControlIconClass()} />
            ) : (
              <Play className={`${videoControlIconClass()} translate-x-[1px]`} />
            )}
          </button>

          {/* زرّ الصوت: يظهر حين يكون الفيديو شغّالاً فعلاً */}
          {allowVideo ? (
            <button
              type="button"
              onClick={() => {
                const video = videoRef.current;
                const next = !soundOn;
                setSoundOn(next);
                if (video) {
                  video.muted = !next;
                  video.defaultMuted = !next;
                  if (next) {
                    video.removeAttribute("muted");
                    video.volume = 1;
                    void video.play().catch(() => {
                      setSoundOn(false);
                      video.muted = true;
                    });
                  } else {
                    video.setAttribute("muted", "");
                  }
                }
              }}
              aria-label={soundOn ? "Mute" : "Unmute"}
              title={soundOn ? "Mute" : "Unmute"}
              className={videoControlButtonClass()}
            >
              {soundOn ? (
                <Volume2 className={videoControlIconClass()} />
              ) : (
                <VolumeX className={videoControlIconClass()} />
              )}
            </button>
          ) : null}
        </VideoControlsBar>
      ) : null}

    </div>
  );
}
